/**
 * "Publicar no meu site" — /comercial/formularios/:id/publicar (ADR-0063).
 *
 * A tela que transforma "precisa de um dev" em "copia e cola": gera o
 * trecho de código já preenchido com o slug e os campos REAIS deste
 * formulário, e deixa o gestor liberar os domínios das landing pages.
 *
 * Três caminhos, e a diferença entre eles importa:
 *   - Opção 1, webhook do construtor (Elementor e afins): o envio sai do
 *     SERVIDOR do site, então não passa por CORS e não exige domínio
 *     cadastrado. O que ele exige é o ID (ou o rótulo, no modo simples do
 *     Elementor) de cada campo batendo com o `mapeia_para` — e isso
 *     ninguém adivinha, por isso a tela mostra o de-para pronto. Fica
 *     marcada "em validação" até as fixtures reais do Elementor passarem
 *     (FORM-11).
 *   - Opção 2, <form> HTML puro: funciona de QUALQUER site, sem
 *     cadastrar nada (formulário nativo não passa por CORS).
 *   - Opção 3, fetch/AJAX: o único que exige o domínio na lista de
 *     origens permitidas, porque aí o envio sai do navegador.
 *
 * Permissão: formulario:manage para salvar; a tela em si é formulario:view.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FiAlertTriangle,
  FiCornerDownRight, FiCheckCircle, FiGlobe, FiPlus, FiTrash2,
} from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import BotaoCopiar from "../../components/BotaoCopiar";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { PageContainer, PageState } from "../../styles/shared";

import {
  Card, Input, Pill, SectionTitle,
} from "../Orcamentos/styles";

import {
  atualizarFormulario,
  CAMPO_REDIRECT_ERRO,
  destinoAceitaVariosCampos,
  destinoEhEndereco,
  PARAMETRO_ERRO,
  testarIngestaoFormulario,
  TIPO_FORMULARIO_LABEL,
  tipoAceitaMapeia,
  tipoGeraLead,
  tipoTemOpcoes,
  nomeExternoDoCampo,
  obterFormulario,
  snippetFetch,
  snippetFormularioHtml,
  urlIngestaoLead,
} from "../../services/formularios";

import { AvisoDoCampo } from "../../components/EnderecoFields/styles";

import FormTabs from "./FormTabs";
import { mesmaOrigem, variantesWwwFaltantes } from "./origens";
import {
  CHAVE_REDIRECT_INGESTAO,
  completarEsquemaHttps,
  erroUrlRedirect,
} from "./urlRedirect";

const Bloco = styled.div`
  margin-bottom: 18px;
`;

const Ajuda = styled.p`
  margin: 6px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.textSecondary};
`;

const Codigo = styled.pre`
  margin: 10px 0 0;
  padding: 14px 16px;
  border-radius: 10px;
  background: ${({ theme }) => theme.codeSurface || "#15171C"};
  color: #e6e6e6;
  font-family: ui-monospace, "JetBrains Mono", monospace;
  font-size: 12.5px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre;
`;

const LinhaTopo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const LinhaOrigem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

// Zero scroll lateral: colunas fixas e texto longo cortado com reticencias
// (o valor inteiro fica no `title`).
const TabelaCampos = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
  margin-top: 8px;

  th, td {
    text-align: left;
    padding: 7px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  th:nth-child(1) { width: 36%; }
  th:nth-child(2) { width: 22%; }
  th:nth-child(3) { width: 26%; }
  th:nth-child(4) { width: 16%; }
  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.textSecondary};
  }
  code {
    font-family: ui-monospace, monospace;
    font-size: 12px;
  }
`;

// Cor comunica estado, nunca decoracao: `danger` fica reservado para o
// caso em que NADA funciona ate o gestor agir (campo obrigatorio sem
// destino no lead). O resto e' aviso, e continua amarelo.
const Aviso = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme, $tone }) => ($tone === "danger" ? theme.dangerSurface : theme.warningSurface)};
  color: ${({ theme, $tone }) => ($tone === "danger" ? theme.dangerText : theme.warningText)};
  border: 1px solid ${({ theme, $tone }) => ($tone === "danger" ? theme.dangerBorder : theme.warningBorder)};
  font-size: 12.5px;
  line-height: 1.5;
  margin-top: 10px;

  svg { flex-shrink: 0; margin-top: 2px; }
`;

function BlocoCodigo({ titulo, descricao, codigo }) {
  const toast = useToast();
  return (
    <Bloco>
      <LinhaTopo>
        <div>
          <SectionTitle style={{ marginBottom: 2 }}>{titulo}</SectionTitle>
          <Ajuda>{descricao}</Ajuda>
        </div>
        <BotaoCopiar
          texto={codigo}
          onErro={() => toast.error("Não consegui copiar. Selecione o texto abaixo.")}
        />
      </LinhaTopo>
      <Codigo>{codigo}</Codigo>
    </Bloco>
  );
}

// Zero scroll lateral: o preview do lead simulado e' uma lista de pares.
const Preview = styled.dl`
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: minmax(90px, 160px) 1fr;
  gap: 4px 12px;
  font-size: 12.5px;

  dt { color: ${({ theme }) => theme.textSecondary}; }
  dd { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const CAMPOS_DO_PREVIEW = [
  ["titulo", "Título"],
  ["contato_nome", "Nome"],
  ["contato_email", "E-mail"],
  ["contato_telefone", "Telefone"],
  ["telefone_secundario", "Telefone 2"],
  ["empresa", "Empresa"],
  ["etapa", "Etapa"],
  ["responsavel", "Responsável"],
  ["origem", "Origem"],
  ["valor_estimado", "Valor estimado"],
  ["observacoes", "Observações"],
];

function textoDoPreview(valor) {
  if (Array.isArray(valor)) return valor.join(", ");
  if (valor === null || valor === undefined || valor === "") return "—";
  return String(valor);
}

/** Lead que o dry-run devolveu (chaves reais de `simular_ingestao`). */
function PreviewDoLead({ lead }) {
  if (!lead) return null;
  const linhas = CAMPOS_DO_PREVIEW.filter(([chave]) => lead[chave] !== undefined);
  const tags = Array.isArray(lead.tags) && lead.tags.length > 0 ? lead.tags.join(", ") : "";
  const endereco = (lead.enderecos || [])[0];
  return (
    <Preview aria-label="Lead que seria criado">
      {linhas.map(([chave, rotulo]) => (
        <React.Fragment key={chave}>
          <dt>{rotulo}</dt>
          <dd title={textoDoPreview(lead[chave])}>{textoDoPreview(lead[chave])}</dd>
        </React.Fragment>
      ))}
      {tags && (<><dt>Tags</dt><dd title={tags}>{tags}</dd></>)}
      {endereco && (
        <>
          <dt>Endereço</dt>
          <dd>{[endereco.logradouro, endereco.numero, endereco.bairro, endereco.cidade, endereco.estado, endereco.cep].filter(Boolean).join(", ") || "—"}</dd>
        </>
      )}
    </Preview>
  );
}

function temCondicao(campo) {
  return Array.isArray(campo?.condicao?.regras) && campo.condicao.regras.length > 0;
}

/**
 * Valor plausivel para o disparo de teste, conforme o tipo do campo.
 *
 * Mandar "Teste de ingestao" em tudo fazia o teste reprovar formularios
 * PERFEITOS: SELECT rejeita valor fora das opcoes, DATA exige AAAA-MM-DD
 * e NUMERO exige numero. O gestor via "integracao quebrada" quando o
 * quebrado era o teste.
 */
function valorDeTeste(campo, nome) {
  // Pelo TIPO primeiro: um EMAIL mapeado a "E-mail 2" recebia "Teste de
  // ingestão" e o proprio teste reprovava o formulario.
  if (campo.tipo === "EMAIL") return "teste@pharu.com.br";
  if (campo.tipo === "TELEFONE") return "11999999999";
  // Endereco: o servidor RECUSA CEP sem 8 digitos e UF que nao existe
  // (FORM-36). Numa lista, vale a opcao do gestor, como para os outros
  // destinos (abaixo).
  if (!tipoTemOpcoes(campo.tipo)) {
    if (nome === "cep") return "01310100";
    if (nome === "estado") return "SP";
  }
  const opcoes = campo.opcoes || [];
  switch (campo.tipo) {
    case "SELECT":
    case "RADIO":
      return opcoes[0] ?? "";
    case "MULTI_SELECT":
    case "CHECKBOX":
      return opcoes.length ? [opcoes[0]] : [];
    case "DATA":
      return new Date().toISOString().slice(0, 10);
    case "NUMERO":
      return "1";
    case "CPF_CNPJ":
      return "11144477735";
    default:
      break;
  }
  if (nome === "contato_email") return "teste@pharu.com.br";
  if (nome === "contato_telefone") return "11999999999";
  return "Teste de ingestão";
}

/**
 * O que a ingestao faz com CEP/UF que nao da para gravar (FORM-36).
 *
 * Os construtores de formulario nao mostram a resposta ao visitante, entao o
 * servidor nao recusa o envio: o lead entra sem o dado e a linha do tempo do
 * lead mostra o que o site mandou. Quem integra so fica sabendo por esta tela
 * que o dado ficou de fora.
 */
function regraDoEnderecoNaIngestao(destinos) {
  const cep = destinos.includes("cep");
  const uf = destinos.includes("estado");
  const consequencia =
    "o lead entra sem esse dado e a linha do tempo do lead mostra o que o site mandou.";
  if (cep && uf) {
    return `O CEP precisa dos 8 dígitos e a UF aceita a sigla (SP) ou o nome do estado. Fora disso, ${consequencia}`;
  }
  if (cep) {
    return `O CEP precisa dos 8 dígitos. Com menos, ${consequencia}`;
  }
  if (uf) {
    return `A UF aceita a sigla (SP) ou o nome do estado. Com uma UF que não existe, ${consequencia}`;
  }
  return "";
}

export default function PublicarFormulario() {
  const { id } = useParams();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();
  const podeEditar = isMaster || hasAllPermissions(["formulario:manage"]);

  const [formulario, setFormulario] = useState(null);
  const [origens, setOrigens] = useState([]);
  const [novaOrigem, setNovaOrigem] = useState("");
  const [obrigadoRascunho, setObrigadoRascunho] = useState("");
  const [erroObrigado, setErroObrigado] = useState("");
  const [antispam, setAntispam] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [testando, setTestando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const dado = await obterFormulario(request, id);
      setFormulario(dado);
      setOrigens(dado.origens_permitidas || []);
      setAntispam(dado.antispam_ativo !== false);
      setObrigadoRascunho((dado.config || {})[CHAVE_REDIRECT_INGESTAO] || "");
      setErroObrigado("");
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar o formulário.");
    } finally {
      setCarregando(false);
    }
  }, [request, id]);

  useEffect(() => { carregar(); }, [carregar]);

  // useMemo proprio: `|| []` cria array novo a cada render e faria os
  // dois useMemo abaixo recalcularem sempre.
  const campos = useMemo(() => formulario?.campos || [], [formulario]);
  const enderecaveis = useMemo(
    () => campos.filter((c) => nomeExternoDoCampo(c)),
    [campos],
  );
  const destinosMapeados = useMemo(
    () => enderecaveis.map(nomeExternoDoCampo),
    [enderecaveis],
  );
  // Duas listas, nao uma: mandar "defina o mapeia para no Editor" para um
  // campo de upload e' uma instrucao impossivel — o Editor nao oferece o
  // seletor para esse tipo.
  const semMapeamento = useMemo(
    () => campos.filter(
      (c) => !nomeExternoDoCampo(c) && c.tipo !== "SECAO"
        && tipoAceitaMapeia(c.tipo),
    ),
    [campos],
  );
  // Os motivos que travam 100% dos envios. Sao os unicos avisos desta
  // tela que exigem acao ANTES de publicar — os dois derrubam qualquer
  // envio, e sem aviso o gestor so descobre pelo integrador reclamando.
  const bloqueios = useMemo(() => {
    const lista = [];

    // O remédio depende do TIPO: campo que aceita mapeamento só precisa
    // ganhar um destino; upload de arquivo não tem esse seletor no Editor
    // — mandar "defina o mapeia para" ali seria mandar fazer o que não
    // existe.
    const obrigatoriosSemDestino = campos.filter(
      (c) => c.obrigatorio && !nomeExternoDoCampo(c) && c.tipo !== "SECAO",
    );
    const mapeaveis = obrigatoriosSemDestino.filter((c) => tipoAceitaMapeia(c.tipo));
    const naoMapeaveis = obrigatoriosSemDestino.filter((c) => !tipoAceitaMapeia(c.tipo));

    if (mapeaveis.length > 0) {
      const nomes = mapeaveis.map((c) => `"${c.label}"`).join(", ");
      lista.push(
        `${nomes} ${mapeaveis.length > 1 ? "estão marcados" : "está marcado"}`
        + " como obrigatório mas não tem destino no lead — um site externo não"
        + " consegue preencher, e o servidor recusa todos os envios. No Editor,"
        + " defina o “mapeia para” ou desmarque como obrigatório.",
      );
    }
    if (naoMapeaveis.length > 0) {
      const nomes = naoMapeaveis.map((c) => `"${c.label}"`).join(", ");
      lista.push(
        `${nomes} ${naoMapeaveis.length > 1 ? "são de tipos que não podem" : "é de um tipo que não pode"}`
        + " ter destino no lead (upload de arquivo)"
        + " e está como obrigatório — nenhum envio de fora passa assim. No"
        + " Editor, desmarque como obrigatório ou troque o tipo do campo.",
      );
    }

    // Dois campos com o mesmo destino gerariam dois inputs de mesmo
    // nome: um sobrescreve o outro, e o servidor recusa a submissão
    // inteira dizendo que o formulário não pode ser endereçado.
    const porDestino = {};
    campos.forEach((c) => {
      const n = nomeExternoDoCampo(c);
      if (n) porDestino[n] = (porDestino[n] || 0) + 1;
    });
    // Observacoes e Bio sao a excecao: la o servidor concatena as respostas,
    // uma linha "Rotulo: valor" por campo, e a ingestao externa aceita
    // (FORM-23). Nos demais destinos, repetir continua travando tudo.
    const repetidos = Object.keys(porDestino).filter(
      (n) => porDestino[n] > 1 && !destinoAceitaVariosCampos(n),
    );
    if (repetidos.length > 0) {
      lista.push(
        `Mais de um campo aponta para o mesmo destino (${repetidos.join(", ")}).`
        + " Não dá para endereçar o formulário de fora assim: ajuste o"
        + " mapeamento no Editor para cada campo ter um destino próprio"
        + " (só Observações e Bio aceitam mais de um campo).",
      );
    }

    return lista;
  }, [campos]);
  // Texto livre com mais de um campo: na pagina hospedada as duas respostas
  // entram (uma linha por campo), mas de fora o nome e o mesmo — e o servidor
  // endereca o PRIMEIRO. Quem integra precisa saber qual e (FORM-23).
  const textoLivreRepetido = useMemo(() => {
    const porDestino = {};
    campos.forEach((c) => {
      const nome = nomeExternoDoCampo(c);
      if (!destinoAceitaVariosCampos(nome)) return;
      porDestino[nome] = porDestino[nome] || [];
      porDestino[nome].push(c.label || "(sem rótulo)");
    });
    return Object.keys(porDestino)
      .filter((nome) => porDestino[nome].length > 1)
      .map((nome) => ({ nome, rotulos: porDestino[nome] }));
  }, [campos]);
  const nuncaEnderecaveis = useMemo(
    () => campos.filter(
      (c) => !nomeExternoDoCampo(c) && c.tipo !== "SECAO"
        && !tipoAceitaMapeia(c.tipo),
    ),
    [campos],
  );
  // Campo mapeado com condicao: de fora so chegam os campos mapeados, entao
  // a regra nao tem como ser avaliada e o servidor trata o campo como
  // sempre visivel (FORM-32). Quem integra precisa saber.
  const mapeadosComCondicao = useMemo(
    () => enderecaveis.filter(temCondicao),
    [enderecaveis],
  );
  const wwwFaltantes = useMemo(() => variantesWwwFaltantes(origens), [origens]);

  async function salvar(novosDados) {
    await atualizarFormulario(request, id, novosDados);
    await carregar();
  }

  // Grava so a pagina de obrigado da LANDING (FORM-50): a pagina hospedada
  // tem o proprio redirecionamento, editado no Editor. O config vai inteiro
  // porque o PATCH substitui o bloco.
  async function salvarPaginaObrigado() {
    const valor = completarEsquemaHttps(obrigadoRascunho);
    const erroUrl = erroUrlRedirect(valor);
    setObrigadoRascunho(valor);
    setErroObrigado(erroUrl);
    if (erroUrl) throw new Error(erroUrl);
    await salvar({
      config: { ...(formulario.config || {}), [CHAVE_REDIRECT_INGESTAO]: valor },
    });
  }

  function completarPaginaObrigado() {
    const valor = completarEsquemaHttps(obrigadoRascunho);
    setObrigadoRascunho(valor);
    setErroObrigado(erroUrlRedirect(valor));
  }

  async function adicionarOrigem() {
    const valor = novaOrigem.trim();
    // `return` silencioso deixava o AsyncButton anunciar "Dominio
    // adicionado." sem ter adicionado nada.
    if (!valor) throw new Error("Informe o domínio.");
    if (origens.some((o) => mesmaOrigem(o, valor))) {
      throw new Error("Este domínio já está na lista.");
    }
    await salvar({ origens_permitidas: [...origens, valor] });
    setNovaOrigem("");
  }

  async function removerOrigem(alvo) {
    await salvar({ origens_permitidas: origens.filter((o) => o !== alvo) });
  }

  async function testarIngestao() {
    setTestando(null);
    const corpo = {};
    enderecaveis.forEach((c) => {
      const nome = nomeExternoDoCampo(c);
      corpo[nome] = valorDeTeste(c, nome);
    });
    try {
      // Pela API autenticada, NAO pelo endereco publico: o ERP e' outra
      // origem e nao esta na allowlist do cliente (nem deve estar), entao
      // o fetch direto morria em "erro de rede" e passava a impressao de
      // integracao quebrada. A action e' um DRY-RUN (FORM-14): roda o mesmo
      // parser, tradutor e service da rota publica e desfaz tudo — devolve
      // o lead que SERIA criado ({ok, lead, avisos, resposta}).
      const resultado = await testarIngestaoFormulario(request, id, corpo, { silentError: true });
      const avisos = Array.isArray(resultado?.avisos) ? resultado.avisos : [];
      setTestando({
        ok: Boolean(resultado?.ok),
        msg: resultado?.ok
          ? "Simulação concluída: este é o lead que o site criaria. Nada foi gravado."
          : avisos.join(" ") || "A simulação encontrou um problema.",
        lead: resultado?.lead || null,
      });
      if (resultado?.ok) toast.success("Ingestão funcionando.");
    } catch (err) {
      // `request` ja traz `detalhe` do backend na mensagem do erro.
      setTestando({
        ok: false,
        msg: err?.message || "Não foi possível testar agora.",
        lead: null,
      });
    }
  }

  async function adicionarVarianteWww(variante) {
    await salvar({ origens_permitidas: [...origens, variante] });
  }

  // Formulario de enriquecimento/generico nao cria lead: o endpoint de
  // ingestao recusa 100% dos envios. Entregar codigo "pronto para colar"
  // que sempre falha e' pior que nao entregar nada.
  const geraLead = tipoGeraLead(formulario?.tipo);

  if (carregando) return <PageState>Carregando…</PageState>;
  if (erro) return <PageState>{erro}</PageState>;
  if (!formulario) return null;

  if (!geraLead) {
    return (
      <PageContainer>
        <EntityHeader
          title={formulario.nome}
          subtitle="Publicar no meu site"
        />
        <FormTabs id={id} ativa="publicar" />
        <Card>
          <SectionTitle>
            <FiAlertTriangle style={{ verticalAlign: "-2px" }} /> Este
            formulário não pode ser publicado num site
          </SectionTitle>
          <Ajuda>
            Só formulários de <strong>Captura de lead</strong> e{" "}
            <strong>Campanha</strong> criam um lead novo. Este é do tipo{" "}
            <strong>{TIPO_FORMULARIO_LABEL[formulario.tipo] || formulario.tipo}</strong>,
            que atualiza um lead que já existe — e um visitante de fora não
            tem como dizer qual. Para captar do seu site, crie um formulário
            de captura em <strong>Comercial → Formulários</strong>.
          </Ajuda>
        </Card>
      </PageContainer>
    );
  }

  // Nunca inventar destino: `origens[0] + "/obrigado"` passava na
  // validacao do backend (que so olha a origem) e levava o visitante a
  // um 404 no site do cliente. O endereco e' o que o gestor digitou.
  const paginaObrigado = (formulario.config || {})[CHAVE_REDIRECT_INGESTAO] || "";
  const redirectNaAllowlist =
    !!paginaObrigado && origens.some((o) => mesmaOrigem(o, paginaObrigado));
  const htmlSnippet = snippetFormularioHtml(formulario.slug, campos, {
    redirect: redirectNaAllowlist ? paginaObrigado : "",
  });

  return (
    <PageContainer>
      <EntityHeader
        title={formulario.nome}
        subtitle="Publicar no meu site"
        badges={[{ label: formulario.ativo ? "Ativo" : "Inativo", tone: formulario.ativo ? "success" : "danger" }]}
      />
      <FormTabs id={id} ativa="publicar" />

      {bloqueios.length > 0 && (
        <Aviso $tone="danger">
          <FiAlertTriangle />
          <span>
            <strong>
              Nenhum envio vai passar enquanto isto não for resolvido.
            </strong>
            {bloqueios.length === 1 ? (
              <> {bloqueios[0]}</>
            ) : (
              <ul style={{ margin: "6px 0 0 18px", listStyle: "disc" }}>
                {bloqueios.map((b) => <li key={b}>{b}</li>)}
              </ul>
            )}
          </span>
        </Aviso>
      )}

      {!formulario.ativo && (
        <Aviso>
          <FiAlertTriangle />
          <span>
            Este formulário está <strong>inativo</strong>: o endereço das
            opções abaixo responde 410 (&quot;Formulário encerrado&quot;) e
            nenhum lead entra — nem pelo teste desta página. Ative na aba
            Editor.
          </span>
        </Aviso>
      )}


      <Card>
        <SectionTitle>Nomes dos campos</SectionTitle>
        <Ajuda>
          Estes são os nomes que o seu site deve usar. Eles vêm do
          <strong> mapeamento para o lead</strong> configurado no Editor —
          então renomear o rótulo de um campo não muda nada aqui. O que muda é
          trocar o próprio &quot;mapeia para&quot;: aí o nome vira outro, e o
          site que usava o antigo para de mandar aquele dado. Se mexer nisso,
          volte e copie o trecho de novo.
        </Ajuda>
        <TabelaCampos>
          <thead>
            <tr>
              <th>Campo no formulário</th>
              <th>Nome a usar no site</th>
              <th>Também aceita</th>
              <th>Obrigatório</th>
            </tr>
          </thead>
          <tbody>
            {enderecaveis.map((c) => {
              const nome = nomeExternoDoCampo(c);
              // Quem traduz os apelidos é o servidor: a lista vem do campo.
              const apelidos = Array.isArray(c.apelidos_ingestao) ? c.apelidos_ingestao : [];
              return (
                <tr key={c.id}>
                  <td title={c.label}>{c.label}</td>
                  <td title={nome}><code>{nome}</code></td>
                  <td title={apelidos.join(", ") || undefined}>
                    {apelidos.length === 0
                      ? "—"
                      : apelidos.map((apelido, i) => (
                        <React.Fragment key={apelido}>
                          {i > 0 && ", "}
                          <code>{apelido}</code>
                        </React.Fragment>
                      ))}
                  </td>
                  <td>{c.obrigatorio ? "Sim" : "Não"}</td>
                </tr>
              );
            })}
          </tbody>
        </TabelaCampos>

        {destinosMapeados.some(destinoEhEndereco) && (
          <Ajuda style={{ marginTop: 10 }}>
            Os campos de endereço vão para o <strong>endereço principal</strong>{" "}
            do lead. {regraDoEnderecoNaIngestao(destinosMapeados)}
          </Ajuda>
        )}

        {semMapeamento.length > 0 && (
          <Aviso>
            <FiAlertTriangle />
            <span>
              {semMapeamento.length} campo(s) sem mapeamento para o lead
              ({semMapeamento.map((c) => c.label).join(", ")}) não podem ser
              enviados de fora. Para incluí-los, defina o
              &quot;mapeia para&quot; deles no Editor.
            </span>
          </Aviso>
        )}

        {mapeadosComCondicao.length > 0 && (
          <Aviso>
            <FiAlertTriangle />
            <span>
              {mapeadosComCondicao.map((c) => `"${c.label}"`).join(", ")}{" "}
              {mapeadosComCondicao.length > 1 ? "têm" : "tem"} condição de
              exibição. A condição só vale na página hospedada: de um site
              externo esses campos são sempre aceitos e vão para o lead
              quando vierem preenchidos.
            </span>
          </Aviso>
        )}

        {textoLivreRepetido.map(({ nome, rotulos }) => (
          <Aviso key={nome}>
            <FiAlertTriangle />
            <span>
              {rotulos.length} campos vão para o mesmo destino
              (&quot;{nome}&quot;): {rotulos.join(", ")}. Na página hospedada as
              respostas entram uma por linha; de um site externo,
              &quot;{nome}&quot; endereça só o primeiro ({rotulos[0]}).
            </span>
          </Aviso>
        ))}

        {nuncaEnderecaveis.length > 0 && (
          <Aviso>
            <FiAlertTriangle />
            <span>
              {nuncaEnderecaveis.map((c) => c.label).join(", ")}: a
              ingestão de site externo não aceita arquivo — o envio entra
              sem ele. Para receber planta ou foto do ambiente, use a página
              hospedada, onde o arquivo sobe de verdade e vira anexo do lead.
            </span>
          </Aviso>
        )}
        {enderecaveis.length === 0 && (
          <Aviso>
            <FiAlertTriangle />
            <span>
              Nenhum campo mapeado para o lead: o site não tem o que enviar.
              Abra o Editor e defina o mapeamento de pelo menos um campo.
            </span>
          </Aviso>
        )}
      </Card>

      <Card>
        <SectionTitle>Como conectar o seu site</SectionTitle>
        <Ajuda>
          Três caminhos para o mesmo destino. Escolha um: o primeiro se o seu
          site já monta os formulários, o segundo se você pode colar HTML, o
          terceiro se você prefere enviar por código.
        </Ajuda>

        <BlocoCodigo
          titulo="Opção 1 — webhook do construtor de formulários (Elementor, WPForms, Contact Form 7…)"
          descricao="Se o seu site já monta o formulário, é só colar este endereço no campo Webhook do construtor. Não precisa cadastrar domínio: o envio sai do servidor do site, não do navegador do visitante."
          codigo={urlIngestaoLead(formulario.slug)}
        />
        <Aviso>
          <FiAlertTriangle />
          <span>
            <Pill $tone="warning">Em validação</Pill>{" "}
            O formato exato que o Elementor envia ainda está sendo conferido
            com um envio real. Depois de configurar, use o teste abaixo e
            confira no CRM o primeiro envio feito pelo site.
          </span>
        </Aviso>
        <Ajuda style={{ marginTop: 10 }}>
          Um passo que ninguém adivinha: cada campo do construtor precisa
          chegar com o nome da tabela acima. O Elementor manda o{" "}
          <strong>rótulo</strong> do campo no modo simples e o{" "}
          <strong>ID</strong> (<em>Avançado → ID</em>) com &quot;Advanced
          Data&quot; ligado — os dois são aceitos, e o rótulo pode ser o do
          seu próprio formulário aqui (maiúsculas e acentos não importam).
          Os metadados que o construtor manda junto (data, página, IP) são
          ignorados; a página de origem vira a referência da campanha.
          {enderecaveis.length > 0 && (
            <>
              {" "}No seu caso:{" "}
              {enderecaveis.map((c, i) => (
                <span key={c.id || i}>
                  {i > 0 && ", "}
                  <strong>{c.label}</strong> → <code>{nomeExternoDoCampo(c)}</code>
                </span>
              ))}
              .
            </>
          )}
        </Ajuda>
        <Ajuda style={{ marginTop: 6 }}>
          E um detalhe que engana: esses construtores enviam sem esperar
          resposta. Se o envio for recusado, o visitante <em>ainda vê</em>{" "}
          “enviado com sucesso” na tela do seu site — por isso confira pelo
          teste logo abaixo das opções.
        </Ajuda>

        <BlocoCodigo
          titulo="Opção 2 — formulário HTML pronto"
          descricao="Funciona em qualquer site, sem cadastrar domínio e sem uma linha de código próprio. Cole no HTML da página."
          codigo={htmlSnippet}
        />

        <BlocoCodigo
          titulo="Opção 3 — envio por JavaScript"
          descricao="Para quem já tem código no site e quer enviar sem sair da página. É o único dos três em que o envio sai do navegador — por isso exige o domínio cadastrado mais abaixo (a página de obrigado também exige, em qualquer opção)."
          codigo={snippetFetch(formulario.slug, campos)}
        />
      </Card>

      <Card>
        <SectionTitle>Conferir se está funcionando</SectionTitle>
        <Ajuda>
          Simula um envio com os campos deste formulário pelo mesmo caminho
          do site e mostra o lead que seria criado — <strong>sem gravar
          nada</strong>: nenhum lead, resposta ou aviso a integrações. Confirma
          o que é comum às três opções: que o formulário aceita esses campos e
          em qual etapa, com qual responsável, o lead nasceria.
          {" "}Ela roda <strong>por dentro do sistema</strong>, então não
          exercita a ponta do seu site — se ela passar e o seu site continuar
          falhando, a causa está do lado de lá: nome de campo no construtor,
          domínio não cadastrado, ou o envio nem chegando a sair.
        </Ajuda>
        {podeEditar ? (
          <div style={{ marginTop: 10 }}>
            <AsyncButton
              variant="secondary"
              onClick={testarIngestao}
              silentSuccess
              silentError
            >
              Testar ingestão (simulação, não grava nada)
            </AsyncButton>
            {testando && (
              <Ajuda style={{ marginTop: 8 }} role="status">
                {testando.ok ? <FiCheckCircle /> : <FiAlertTriangle />}{" "}
                {testando.msg}
              </Ajuda>
            )}
            {testando && <PreviewDoLead lead={testando.lead} />}
          </div>
        ) : (
          <Ajuda style={{ marginTop: 10 }}>
            Só quem gerencia formulários pode rodar a simulação.
          </Ajuda>
        )}
      </Card>

      <Card>
        <SectionTitle>
          <FiCornerDownRight style={{ verticalAlign: "-2px" }} /> Página de
          obrigado
        </SectionTitle>
        <Ajuda>
          Vale só para a landing do seu site, na <strong>Opção 2</strong>: sem
          JavaScript, o visitante vê a resposta em texto puro depois de
          enviar, e este endereço leva ele para uma página sua. O destino
          precisa ser de um domínio da lista abaixo, senão é ignorado — a
          regra impede que o endereço da Pharu vire redirecionador para
          qualquer lugar.
        </Ajuda>
        <Ajuda style={{ marginTop: 6 }}>
          Nas opções 1 e 3 ele não é usado: no construtor quem decide para
          onde ir depois do envio é o próprio construtor, e no JavaScript é o
          seu código. <strong>Ao mudar este endereço, volte à Opção 2 e copie
          o trecho de novo</strong> — ele já sai com o valor embutido.
        </Ajuda>
        <Ajuda style={{ marginTop: 6 }}>
          A página hospedada deste formulário (<code>/f/{formulario.slug}</code>)
          não usa este endereço: o redirecionamento dela é a URL de
          redirecionamento do Editor.
        </Ajuda>
        <Ajuda style={{ marginTop: 6 }}>
          <strong>E quando dá erro?</strong> O trecho da Opção 2 manda também
          o campo escondido <code>{CAMPO_REDIRECT_ERRO}</code> com o endereço
          da própria página. Se o domínio dela estiver na lista abaixo, o
          visitante volta para lá com <code>?{PARAMETRO_ERRO}=</code> e o
          motivo (ex.: <code>validacao</code>, <code>limite</code>); senão vê
          uma página simples com a mensagem e um botão Voltar — nunca a tela
          técnica da API. Sem página de obrigado, o sucesso também mostra uma
          página simples &quot;Recebemos seu contato&quot;.
        </Ajuda>
        <LinhaOrigem>
          <Input
            id="publicar-pagina-obrigado"
            aria-label="Página de obrigado"
            value={obrigadoRascunho}
            onChange={(e) => setObrigadoRascunho(e.target.value)}
            onBlur={completarPaginaObrigado}
            placeholder="https://www.seusite.com.br/obrigado"
            style={{ maxWidth: 420 }}
            disabled={!podeEditar}
            aria-invalid={Boolean(erroObrigado)}
            aria-describedby={erroObrigado ? "publicar-pagina-obrigado-erro" : undefined}
          />
          {podeEditar && (
            // silentError: a URL invalida aparece no proprio campo, e erro da
            // API ja sai no toast automatico do request.
            <AsyncButton
              variant="secondary"
              successMessage="Página de obrigado salva."
              onClick={salvarPaginaObrigado}
              silentError
            >
              Salvar
            </AsyncButton>
          )}
        </LinhaOrigem>
        {erroObrigado && (
          <AvisoDoCampo id="publicar-pagina-obrigado-erro" $tom="danger" role="alert">
            {erroObrigado}
          </AvisoDoCampo>
        )}
        {paginaObrigado && !redirectNaAllowlist && (
          <Aviso style={{ marginTop: 10 }}>
            <FiAlertTriangle />
            <span>
              <strong>{paginaObrigado}</strong> não está entre os domínios
              permitidos, então o redirecionamento é ignorado e o snippet sai
              sem ele. Cadastre o domínio abaixo.
            </span>
          </Aviso>
        )}
      </Card>

      <Card>
        <SectionTitle><FiGlobe style={{ verticalAlign: "-2px" }} /> Domínios permitidos</SectionTitle>
        <Ajuda>
          Necessário só para a <strong>Opção 3</strong> (JavaScript) e para a
          página de obrigado acima. As opções 1 e 2 funcionam sem cadastrar
          nada. Um domínio aqui vale apenas para este formulário, não para o
          resto do sistema.
        </Ajuda>

        {origens.map((origem) => (
          <LinhaOrigem key={origem}>
            <Pill $tone="info">{origem}</Pill>
            {podeEditar && (
              <AsyncButton
                variant="icon"
                onClick={() => removerOrigem(origem)}
                successMessage="Domínio removido."
              >
                <FiTrash2 /> Remover
              </AsyncButton>
            )}
          </LinhaOrigem>
        ))}
        {origens.length === 0 && (
          <Ajuda style={{ marginBottom: 10 }}>
            Nenhum domínio cadastrado ainda.
          </Ajuda>
        )}
        {wwwFaltantes.map((variante) => (
          <Aviso key={variante} style={{ marginTop: 0, marginBottom: 10 }}>
            <FiAlertTriangle />
            <span>
              Para o navegador, <strong>{variante}</strong> é outro domínio:
              se a landing abrir por esse endereço, o envio por JavaScript e a
              página de obrigado falham.
              {podeEditar && (
                <>
                  {" "}
                  <AsyncButton
                    variant="secondary"
                    onClick={() => adicionarVarianteWww(variante)}
                    successMessage="Domínio adicionado."
                  >
                    Adicionar {variante}
                  </AsyncButton>
                </>
              )}
            </span>
          </Aviso>
        ))}

        {podeEditar && (
          <LinhaOrigem style={{ marginTop: 10 }}>
            <Input
              value={novaOrigem}
              onChange={(e) => setNovaOrigem(e.target.value)}
              placeholder="https://www.seusite.com.br"
              style={{ maxWidth: 340 }}
            />
            <AsyncButton
              variant="secondary"
              onClick={adicionarOrigem}
              successMessage="Domínio adicionado."
            >
              <FiPlus /> Adicionar
            </AsyncButton>
          </LinhaOrigem>
        )}
      </Card>

      <Card>
        <SectionTitle>Proteção contra robôs</SectionTitle>
        <Ajuda>
          Com isto ligado, dois sinais descartam envios automáticos: o campo
          escondido <code>_gotcha</code>, que só um robô preenche, e o
          preenchimento rápido demais (menos de dois segundos, medido no
          próprio navegador do visitante e enviado em <code>_tempo_ms</code>).
          O trecho da Opção 2 já traz os dois; o da Opção 3 mede o tempo e
          envia o <code>_gotcha</code> se você colocar no seu formulário o
          campo escondido indicado no comentário do trecho. O robô recebe uma
          resposta de sucesso e nada é criado — não avisamos que ele foi pego.
          {" "}No caminho da <strong>Opção 1</strong>, quem monta o formulário
          é o seu construtor: para a isca valer ali, acrescente um campo
          escondido chamado <code>_gotcha</code> nele. A página hospedada
          (<code>/f/{formulario.slug}</code>) usa a mesma proteção.
        </Ajuda>
        <LinhaOrigem style={{ marginTop: 10 }}>
          <Pill $tone={antispam ? "success" : "neutral"}>
            {antispam ? "Ligada" : "Desligada"}
          </Pill>
          {podeEditar && (
            <AsyncButton
              variant="secondary"
              onClick={() => salvar({ antispam_ativo: !antispam })}
              successMessage="Proteção atualizada."
            >
              {antispam ? "Desligar" : "Ligar"}
            </AsyncButton>
          )}
        </LinhaOrigem>
      </Card>

    </PageContainer>
  );
}
